import axios from 'axios';

export const userProgressService = {
    async getUserProgress(subscriptionId, testId) {
        try {
            console.log('Fetching progress for:', { subscriptionId, testId });

            if (!subscriptionId || !testId) {
                throw new Error('Subscription ID and test ID are required');
            }

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/userProgress/subscription/${subscriptionId}`, {
                params: { test: testId },
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
            });

            console.log('Progress response:', response.data);
            return response.data.data.data;
        } catch (error) {
            console.error('User progress error:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            throw error;
        }
    },

    async updateUserProgress(progressId, progressData) {
        try {
            console.log('Updating progress:', { progressId, progressData });

            if (!progressId) {
                throw new Error('Progress ID is required');
            }

            // Ensure we're sending all the necessary fields to preserve state
            // Validate and sanitize data before sending
            const fullProgressData = {
                correctAnswers: progressData.correctAnswers ?? 0,
                inCorrectAnswers: progressData.inCorrectAnswers ?? 0,
                currentQuestionIndex: progressData.currentQuestionIndex ?? 0,
                questionOrder: Array.isArray(progressData.questionOrder) ? progressData.questionOrder : [],
                lastQuestionId: progressData.lastQuestionId ?? null,
                attemptedQuestions: Array.isArray(progressData.attemptedQuestions) ? progressData.attemptedQuestions : [],
                firstAttemptCorrect: Array.isArray(progressData.firstAttemptCorrect) ? progressData.firstAttemptCorrect : [],
                missedQuestions: Array.isArray(progressData.missedQuestions) ? progressData.missedQuestions : [],
            };

            const response = await axios.patch(`${process.env.REACT_APP_API_URL}/api/userProgress/${progressId}`, fullProgressData, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json',
                },
            });

            console.log('Update response:', response.data);
            return response.data.data.data;
        } catch (error) {
            console.error('Error updating progress:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            throw error;
        }
    },

    async updateUserProgressWithRetry(progressId, progressData, maxRetries = 3) {
        let attempt = 0;
        let lastError;

        while (attempt < maxRetries) {
            try {
                const response = await this.updateUserProgress(progressId, progressData);
                return response;
            } catch (error) {
                lastError = error;
                attempt++;

                if (error.response?.status === 429) {
                    // Rate limited - wait longer between retries
                    const retryAfter = parseInt(error.response.headers['retry-after']) || 5;
                    await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000));
                } else if (attempt === maxRetries) {
                    // Don't retry on other errors or if we're out of retries
                    throw error;
                } else {
                    // Wait a bit before retrying on other errors
                    await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
                }
            }
        }

        throw lastError;
    },

    async createUserProgress(subscriptionId, testId) {
        try {
            console.log('Creating new progress:', { subscriptionId, testId });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/userProgress`,
                {
                    subscription: subscriptionId,
                    test: testId,
                    correctAnswers: 0,
                    inCorrectAnswers: 0,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json',
                    },
                },
            );

            console.log('Create response:', response.data);
            return response.data.data.data;
        } catch (error) {
            console.error('Error creating progress:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
            });
            throw error;
        }
    },
};
